import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { getClient, createReview } from '@/services/api';
import { toast } from 'sonner';
import { ArrowLeft, Star, Send, Eye, EyeOff, AlertCircle } from 'lucide-react';

const StarRating = ({ value, onChange, label }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="space-y-2">
      <Label className="text-stone-700">{label}</Label>
      <div className="flex items-center gap-1 star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(star)}
            className="p-1 focus:outline-none"
            data-testid={`star-${label.toLowerCase().replace(/[^a-z]/g, '-')}-${star}`}
          >
            <Star 
              className={`w-8 h-8 transition-colors ${
                star <= (hovered || value) 
                  ? 'fill-[#D97757] text-[#D97757]' 
                  : 'text-stone-300'
              }`}
            />
          </button>
        ))}
        <span className="ml-2 text-sm font-medium text-stone-600">
          {value > 0 ? `${value}/5` : 'Not rated'}
        </span>
      </div>
    </div>
  );
};

export default function AddReviewPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreedToGuidelines, setAgreedToGuidelines] = useState(false);
  
  const [formData, setFormData] = useState({
    service_quality_rating: 0,
    punctuality_rating: 0,
    payment_rating: 0,
    communication_rating: 0,
    service_notes: '',
    general_notes: '',
    is_anonymous: false
  });

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const data = await getClient(clientId);
        setClient(data);
      } catch (error) {
        console.error('Error fetching client:', error);
        toast.error('Failed to load client');
        navigate('/clients');
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [clientId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate ratings
    if (formData.service_quality_rating === 0 || 
        formData.punctuality_rating === 0 || 
        formData.payment_rating === 0 || 
        formData.communication_rating === 0) {
      toast.error('Please rate all categories');
      return;
    }

    if (!agreedToGuidelines) {
      toast.error('Please agree to the review guidelines');
      return;
    }

    setSubmitting(true);
    try {
      await createReview({
        client_id: clientId,
        ...formData
      });
      toast.success('Review submitted successfully!');
      navigate(`/clients/${clientId}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit review';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center text-stone-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!client) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center text-stone-500">Client not found</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="add-review-page">
        {/* Back Button */}
        <Link 
          to={`/clients/${clientId}`}
          className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Client
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-semibold text-stone-900 mb-2">
            Review {client.first_name} {client.last_name || (client.last_initial ? `${client.last_initial}.` : '')}
          </h1>
          <p className="text-stone-600">Share your experience to help other professionals</p>
        </div>

        <Card className="border-stone-100 shadow-sm" data-testid="review-form-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-stone-900">Rating Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Rating Categories */}
              <div className="space-y-6">
                <StarRating
                  label="Service Quality & Cooperation"
                  value={formData.service_quality_rating}
                  onChange={(val) => setFormData({ ...formData, service_quality_rating: val })}
                />
                <StarRating
                  label="Punctuality & Reliability"
                  value={formData.punctuality_rating}
                  onChange={(val) => setFormData({ ...formData, punctuality_rating: val })}
                />
                <StarRating
                  label="Payment Behavior"
                  value={formData.payment_rating}
                  onChange={(val) => setFormData({ ...formData, payment_rating: val })}
                />
                <StarRating
                  label="Communication & Attitude"
                  value={formData.communication_rating}
                  onChange={(val) => setFormData({ ...formData, communication_rating: val })}
                />
              </div>

              {/* Notes */}
              <div className="space-y-4 pt-6 border-t border-stone-200">
                <div className="space-y-2">
                  <Label htmlFor="general_notes" className="text-stone-700 text-base font-medium">Your Review</Label>
                  <Textarea
                    id="general_notes"
                    value={formData.general_notes}
                    onChange={(e) => setFormData({ ...formData, general_notes: e.target.value })}
                    placeholder="Share your experience with this client. What should other professionals know?"
                    className="min-h-[120px] rounded-lg border-stone-200 text-base"
                    data-testid="general-notes-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service_notes" className="text-stone-700 text-sm">Service Details (Optional)</Label>
                  <Textarea
                    id="service_notes"
                    value={formData.service_notes}
                    onChange={(e) => setFormData({ ...formData, service_notes: e.target.value })}
                    placeholder="Technical notes: hair/skin/nail condition, products used, special requirements..."
                    className="min-h-[80px] rounded-lg border-stone-200 text-sm"
                    data-testid="service-notes-input"
                  />
                </div>
              </div>

              {/* Anonymous Toggle */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-stone-50 border border-stone-100">
                <div className="flex items-center gap-3">
                  {formData.is_anonymous ? (
                    <EyeOff className="w-5 h-5 text-stone-500" />
                  ) : (
                    <Eye className="w-5 h-5 text-stone-500" />
                  )}
                  <div>
                    <p className="font-medium text-stone-900">Post Anonymously</p>
                    <p className="text-sm text-stone-500">Your name won&apos;t be shown with this review</p>
                  </div>
                </div>
                <Switch
                  checked={formData.is_anonymous}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_anonymous: checked })}
                  data-testid="anonymous-switch"
                />
              </div>

              {/* Review Guidelines Agreement */}
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-stone-900 mb-2">Review Guidelines</p>
                      <ul className="text-sm text-stone-600 space-y-1 list-disc list-inside">
                        <li>Be professional and factual in your review</li>
                        <li>Only share your direct experience with this client</li>
                        <li>Do not include defamatory, discriminatory, or false statements</li>
                        <li>Avoid personal attacks or offensive language</li>
                        <li>Do not share confidential client information beyond service details</li>
                      </ul>
                    </div>
                    <div className="flex items-start gap-2 pt-2 border-t border-amber-200">
                      <Checkbox
                        id="guidelines"
                        checked={agreedToGuidelines}
                        onCheckedChange={setAgreedToGuidelines}
                        className="mt-0.5"
                        data-testid="guidelines-checkbox"
                      />
                      <label htmlFor="guidelines" className="text-sm text-stone-700 cursor-pointer">
                        I confirm this review is truthful, professional, and based on my direct experience. I understand that false or defamatory reviews may be removed and could result in account suspension.
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                className="w-full h-12 rounded-full bg-[#D97757] text-white hover:bg-[#C06040] font-medium text-base"
                disabled={submitting}
                data-testid="submit-review-btn"
              >
                {submitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Review
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
